import { useState, useRef, useEffect, useCallback } from "react"
import { X, Send, Sparkles, StopCircle, RotateCcw, Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { AiMessage } from "./AiMessage"
import { AiSuggestion } from "./AiSuggestion"
import { DOMParser as ProseMirrorDOMParser } from "@tiptap/pm/model"
import { useAiStream } from "@/hooks/use-ai-stream"
import { sanitizeAiHtml } from "@/lib/sanitize-html"

function extractSuggestion(content) {
  const match = content.match(
    /<article-suggestion>([\s\S]*?)<\/article-suggestion>/
  )
  if (match) {
    const suggestion = match[1].trim()
    const conversational = content
      .replace(/<article-suggestion>[\s\S]*?<\/article-suggestion>/, "")
      .trim()
    return { suggestion, conversational }
  }
  return { suggestion: null, conversational: content }
}

function prepareChatHistory(messages) {
  return messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((msg) => {
      if (
        msg.role === "assistant" &&
        msg.content.includes("<article-suggestion>")
      ) {
        const stripped = msg.content.replace(
          /<article-suggestion>[\s\S]*?<\/article-suggestion>/g,
          "[Suggestion applied/discarded]"
        )
        return { role: msg.role, content: stripped }
      }
      return { role: msg.role, content: msg.content }
    })
    .slice(-20)
}

export function AiChatPanel({
  articleId,
  baseUrl,
  editor,
  selectedParagraph,
  setSelectedParagraph,
  onClose,
}) {
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState("")
  const [error, setError] = useState(null)
  const messagesContainerRef = useRef(null)
  const inputRef = useRef(null)
  const pendingChunksRef = useRef("")
  const rafRef = useRef(null)
  const { stream, cancel, isStreaming } = useAiStream()

  // Auto-scroll to bottom (scoped to chat container only)
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100
    if (isNearBottom || isStreaming) {
      container.scrollTop = container.scrollHeight
    }
  }, [messages, isStreaming])

  // Focus input when panel opens
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Clear conversation when switching between full-article mode and paragraph
  // mode, or when switching to a different paragraph. Without this, stale chat
  // history from a full-article conversation ("write a 3-paragraph article")
  // bleeds into the paragraph-editing context and the AI interprets prompts
  // like "make shorter" against the old conversation instead of the selected
  // paragraph.
  const prevParagraphRef = useRef(selectedParagraph)
  useEffect(() => {
    const prev = prevParagraphRef.current
    const curr = selectedParagraph

    // Detect mode switch: null→selected, selected→null, or different paragraph
    const switched =
      (!prev && curr) ||
      (prev && !curr) ||
      (prev && curr && prev.from !== curr.from)

    if (switched && messages.length > 0) {
      cancel() // abort any in-flight stream
      setMessages([])
      setError(null)
      setInput("")
    }

    prevParagraphRef.current = curr
  }, [selectedParagraph]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSend = useCallback(async () => {
    const trimmed = input.trim()
    if (!trimmed || isStreaming) return

    setError(null)
    const userMessage = { role: "user", content: trimmed }
    const aiPlaceholder = { role: "assistant", content: "", isStreaming: true }

    setMessages((prev) => [...prev, userMessage, aiPlaceholder])
    setInput("")

    const chatHistory = prepareChatHistory([...messages, userMessage])

    pendingChunksRef.current = ""

    await stream({
      articleId,
      prompt: trimmed,
      paragraph: selectedParagraph?.html || null,
      articleContent: editor?.getHTML() || null,
      chatHistory,
      baseUrl,
      onChunk: (content) => {
        pendingChunksRef.current += content

        if (!rafRef.current) {
          rafRef.current = requestAnimationFrame(() => {
            const pending = pendingChunksRef.current
            pendingChunksRef.current = ""
            rafRef.current = null

            setMessages((prev) => {
              const updated = [...prev]
              const last = { ...updated[updated.length - 1] }
              last.content += pending
              updated[updated.length - 1] = last
              return updated
            })
          })
        }
      },
      onDone: () => {
        // Flush any remaining chunks
        if (pendingChunksRef.current) {
          const remaining = pendingChunksRef.current
          pendingChunksRef.current = ""
          setMessages((prev) => {
            const updated = [...prev]
            const last = { ...updated[updated.length - 1] }
            last.content += remaining
            last.isStreaming = false
            updated[updated.length - 1] = last
            return updated
          })
        } else {
          setMessages((prev) => {
            const updated = [...prev]
            const last = { ...updated[updated.length - 1] }
            last.isStreaming = false
            updated[updated.length - 1] = last
            return updated
          })
        }
      },
      onError: (errorMsg) => {
        setError(errorMsg)
        setMessages((prev) => {
          const updated = [...prev]
          const last = { ...updated[updated.length - 1] }
          last.isStreaming = false
          if (!last.content) {
            // Remove empty AI placeholder
            return updated.slice(0, -1)
          }
          updated[updated.length - 1] = last
          return updated
        })
      },
    })
  }, [
    input,
    isStreaming,
    messages,
    stream,
    articleId,
    selectedParagraph,
    baseUrl,
  ])

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleApplyFullArticle = useCallback(
    (sanitizedHtml) => {
      if (!editor) return
      const { from } = editor.state.selection
      editor.commands.setContent(sanitizedHtml)
      const maxPos = editor.state.doc.content.size
      editor.commands.setTextSelection(Math.min(from, maxPos))
      // Content sync handled by editor's update event → onChange
    },
    [editor]
  )

  const handleApplyParagraph = useCallback(
    (sanitizedHtml) => {
      if (!selectedParagraph || !editor) return

      const { from, to } = selectedParagraph
      const { state } = editor

      // Validate position + content hash before applying
      const currentNode = state.doc.nodeAt(from)

      if (!currentNode) {
        setError("The paragraph was removed. Please re-select.")
        return
      }

      if (currentNode.type.name !== selectedParagraph.nodeType) {
        setError("The document structure changed. Please re-select the paragraph.")
        return
      }

      const currentHash = currentNode.textContent.slice(0, 50)
      if (currentHash !== selectedParagraph.contentHash) {
        setError("The paragraph content changed. Please re-select it.")
        return
      }

      // Parse AI HTML into ProseMirror nodes via the schema
      const element = document.createElement("div")
      element.innerHTML = sanitizedHtml.trim()
      const parsedDoc = ProseMirrorDOMParser.fromSchema(state.schema).parse(element)

      // Atomic replacement — no position shifting, no hidden adjustments
      const tr = state.tr
      tr.replaceWith(from, to, parsedDoc.content)
      editor.view.dispatch(tr)

      // Content sync handled by editor's update event → onChange
      setSelectedParagraph(null)
    },
    [editor, selectedParagraph, setSelectedParagraph]
  )

  const handleApply = useCallback(
    (sanitizedHtml) => {
      if (selectedParagraph) {
        handleApplyParagraph(sanitizedHtml)
      } else {
        handleApplyFullArticle(sanitizedHtml)
      }
    },
    [selectedParagraph, handleApplyParagraph, handleApplyFullArticle]
  )

  const handleApplied = useCallback((index) => {
    setMessages((prev) => {
      const updated = [...prev]
      const msg = { ...updated[index] }
      msg.applied = true
      updated[index] = msg
      return updated
    })
  }, [])

  const handleDiscard = useCallback((index) => {
    setMessages((prev) => {
      const updated = [...prev]
      const msg = { ...updated[index] }
      msg.discarded = true
      updated[index] = msg
      return updated
    })
  }, [])

  const handleRetry = useCallback(() => {
    setError(null)
    // Find last user message and resend
    const lastUserIdx = messages.findLastIndex((m) => m.role === "user")
    if (lastUserIdx >= 0) {
      setInput(messages[lastUserIdx].content)
      setMessages((prev) => prev.slice(0, lastUserIdx))
    }
  }, [messages])

  return (
    <div className="flex flex-col h-full border-l border-zinc-200 bg-zinc-50">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-kf-red" />
          <span className="text-sm font-semibold text-zinc-900">
            AI Assistant
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-600"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Paragraph context indicator */}
      {selectedParagraph && (
        <div className="flex items-center gap-2 border-b border-zinc-200 bg-kf-red/5 px-4 py-2">
          <span className="text-xs text-kf-red font-medium flex-1 truncate">
            Editing: {selectedParagraph.text?.slice(0, 50)}
            {selectedParagraph.text?.length > 50 ? "..." : ""}
          </span>
          <button
            onClick={() => setSelectedParagraph(null)}
            className="text-zinc-400 hover:text-zinc-600"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Messages area */}
      <div data-tour="ai-chat-messages" ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Sparkles className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">
              {selectedParagraph
                ? "Ask me to edit the selected paragraph"
                : "Ask me to help refine your article"}
            </p>
            <p className="text-xs text-zinc-400 mt-1">
              I'll use your writing style guidelines and KF voice
            </p>
          </div>
        )}

        {messages.map((msg, idx) => {
          if (msg.role === "user") {
            return <AiMessage key={idx} role="user" content={msg.content} />
          }

          // AI message - check for suggestion
          const { suggestion, conversational } = extractSuggestion(msg.content)

          return (
            <div key={idx} className="space-y-2">
              {conversational && (
                <AiMessage
                  role="assistant"
                  content={conversational}
                  isStreaming={msg.isStreaming}
                />
              )}
              {suggestion && !msg.discarded && !msg.applied && !msg.isStreaming && (
                <AiSuggestion
                  html={suggestion}
                  isParagraph={!!selectedParagraph}
                  onApply={(sanitizedHtml) => {
                    handleApply(sanitizedHtml)
                    handleApplied(idx)
                  }}
                  onDiscard={() => handleDiscard(idx)}
                />
              )}
              {msg.applied && suggestion && (
                <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-md px-3 py-1.5">
                  <Check className="h-3 w-3" />
                  <span>Suggestion applied</span>
                </div>
              )}
              {msg.isStreaming && !conversational && (
                <AiMessage
                  role="assistant"
                  content={msg.content}
                  isStreaming={true}
                />
              )}
            </div>
          )
        })}

        {/* Error display */}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-red-600 hover:text-red-800"
              onClick={handleRetry}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </div>
        )}

      </div>

      {/* Input area */}
      <div data-tour="ai-chat-input" className="border-t border-zinc-200 bg-white p-3">
        <div className="flex gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              selectedParagraph
                ? "How should this paragraph change?"
                : "Ask about your article..."
            }
            disabled={isStreaming}
            rows={2}
            className="resize-none text-sm"
          />
          <div className="flex flex-col gap-1">
            {isStreaming ? (
              <Button
                variant="outline"
                size="sm"
                className="h-full px-3 text-red-600"
                onClick={cancel}
              >
                <StopCircle className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                variant="gradient"
                size="sm"
                className="h-full px-3"
                onClick={handleSend}
                disabled={!input.trim()}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
